import type { ReactNode } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useAccountTimezone } from '@/hooks/use-account-timezone'
import { formatInTz } from '@/lib/format'

export type DateDisplayMode = 'date' | 'time' | 'dateTime' | 'split'

const MODE_FORMAT: Record<Exclude<DateDisplayMode, 'split'>, string> = {
  date: 'MMM dd, yyyy',
  time: 'HH:mm',
  dateTime: 'MMM dd, yyyy HH:mm',
}

const SKELETON_WIDTH: Record<DateDisplayMode, number> = {
  date: 96,
  time: 48,
  dateTime: 144,
  split: 96,
}

export type DateDisplayProps = {
  value: Date | string | null | undefined
  mode?: DateDisplayMode
  format?: string
  fallback?: ReactNode
  className?: string
}

export const DateDisplay = ({
  value,
  mode = 'dateTime',
  format,
  fallback,
  className,
}: DateDisplayProps) => {
  const { t } = useTranslation('common')
  const { timezone, isReady } = useAccountTimezone()

  if (value === null || value === undefined) {
    return <span className={className}>{fallback ?? t('notAvailable')}</span>
  }

  if (!isReady) {
    return (
      <span className={className}>
        <Skeleton width={SKELETON_WIDTH[mode]} height={14} />
        {mode === 'split' && (
          <>
            <br />
            <Skeleton width={SKELETON_WIDTH.time} height={12} />
          </>
        )}
      </span>
    )
  }

  if (format) {
    return <span className={className}>{formatInTz(value, format, timezone)}</span>
  }

  if (mode === 'split') {
    return (
      <span className={cn('flex flex-col', className)}>
        <span>{formatInTz(value, MODE_FORMAT.date, timezone)}</span>
        <span className="text-muted-foreground text-xs">
          {formatInTz(value, MODE_FORMAT.time, timezone)}
        </span>
      </span>
    )
  }

  return <span className={className}>{formatInTz(value, MODE_FORMAT[mode], timezone)}</span>
}
